// src/services/BaseApi.js
import axios from "axios";
import { API_URL } from "@/config";
import {
  getAccessToken,
} from "./authStorage";
import { getValidAccessToken, refreshAccessToken } from "./authRefresh";
import { notifyApiException } from "./apiErrorToast";

function isAuthEndpoint(config) {
  const url = config?.url || "";
  return url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
}

function redirectToForbidden() {
  if (window.location.pathname !== "/forbidden") {
    window.location.href = "/forbidden";
  }
}

class BaseApi {
  constructor(basePath = "") {
    this.client = axios.create({
      baseURL: `${API_URL}${basePath}`,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.client.interceptors.request.use(async (config) => {
      try {
        const accessToken = isAuthEndpoint(config)
          ? getAccessToken()
          : await getValidAccessToken(getAccessToken());
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      } catch (error) {
        notifyApiException(error);
        return Promise.reject(error);
      }
    });

    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;
        if (
          error?.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !isAuthEndpoint(originalRequest)
        ) {
          originalRequest._retry = true;
          try {
            const accessToken = await refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            notifyApiException(refreshError, originalRequest?.url);
            return Promise.reject(refreshError);
          }
        }
        notifyApiException(error, originalRequest?.url);
        if (error?.response?.status === 403 && !isAuthEndpoint(originalRequest)) {
          redirectToForbidden();
        }
        return Promise.reject(error);
      }
    );
  }

  get(url, params = {}) {
    return this.client.get(url, { params });
  }

  post(url, body = {}) {
    return this.client.post(url, body);
  }

  put(url, body = {}) {
    return this.client.put(url, body);
  }
}

export default BaseApi;
